/**
 * <mj-product-grid> -- turns the Load more link into appending.
 *
 * The link works without this file: it points at the next page, and following it is
 * ordinary pagination. What this adds is fetching that page, taking its grid items, and
 * putting them on the end of the current list -- which is what the comp's running
 * "Showing 40 of 138" describes.
 *
 * It reads the next page's markup rather than asking for JSON, so the cards are built by
 * the same Liquid as the ones already on screen and cannot drift from them.
 */

class MjProductGrid extends HTMLElement {
  connectedCallback() {
    this.list = this.querySelector('[data-mj-grid-list]');
    this.button = this.querySelector('[data-mj-grid-load]');
    this.shown = this.querySelector('[data-mj-grid-shown]');
    if (!this.list || !this.button) return;

    this.button.addEventListener('click', (event) => {
      event.preventDefault();
      this.#loadMore();
    });
  }

  async #loadMore() {
    const url = this.button.getAttribute('href');
    if (!url || this.busy) return;

    this.busy = true;
    this.button.setAttribute('aria-busy', 'true');

    try {
      const response = await fetch(url);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const markup = await response.text();
      const next = new DOMParser().parseFromString(markup, 'text/html');

      const items = next.querySelectorAll('[data-mj-grid-list] > li');
      if (items.length === 0) throw new Error('no items on the next page');

      // The editorial cards are placed by position from the top of the collection and are
      // only rendered on page one, so anything that arrives here is a product.
      const focusTarget = items[0];
      items.forEach((item) => this.list.appendChild(document.importNode(item, true)));

      if (this.shown) {
        this.shown.textContent = this.list.querySelectorAll(':scope > li:not(.mj-grid__item--promo)').length;
      }

      // Hand the keyboard the first of the new cards; otherwise focus stays on a button
      // that may have just disappeared, and a screen reader is told nothing happened.
      const landed = this.list.querySelector(`[data-product-id="${focusTarget.querySelector('[data-product-id]')?.dataset.productId}"]`);
      const link = landed?.querySelector('a[href]');
      if (link) {
        link.setAttribute('tabindex', '-1');
        link.focus({ preventScroll: true });
      }

      const nextButton = next.querySelector('[data-mj-grid-load]');
      if (nextButton) {
        this.button.setAttribute('href', nextButton.getAttribute('href'));
      } else {
        this.button.remove();
      }
    } catch {
      // Let the link be a link: a failed fetch should still take the shopper to page two
      // rather than leave a button that does nothing.
      window.location.href = url;
      return;
    } finally {
      this.busy = false;
      this.button.removeAttribute('aria-busy');
    }
  }
}

if (!customElements.get('mj-product-grid')) {
  customElements.define('mj-product-grid', MjProductGrid);
}
